// היומן — the calendar screen.
//
// ONE FETCH, SIX WAYS OF LOOKING AT IT. The three ranges differ only in the window they
// ask for, the two modes only in how that window is drawn, and the partner filter only in
// which of the fetched rows are shown. Nothing but the range is a fetch dependency, so
// switching the rendering or the partner is instant and can never disagree with what the
// other half would have shown.
//
// THE SEARCH IS THE ONE OTHER FETCH (077, MeetingSearch): across every date rather than a
// window, and while it is on, its results replace the window.
//
// IT TOUCHES NO MONEY. A meeting has no amount, no calculator reads the table, and nothing
// on this screen is derived from an invoice, an expense or the partner balance.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { authApi } from '../../api';
import { meetingsApi } from '../../api/calendar';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import SearchBox from '../../components/SearchBox';
import TaskModal from '../../components/TaskModal';
import CalendarNav from './CalendarNav';
import CalendarViewBar from './CalendarViewBar';
import TimeGrid from './TimeGrid';
import MonthGrid from './MonthGrid';
import MeetingList from './MeetingList';
import MeetingModal from './MeetingModal';
import MeetingDetail from './MeetingDetail';
import CancelMeetingModal from './CancelMeetingModal';
import MeetingDeleteModal from './MeetingDeleteModal';
import LinkMeetingsDialog from './LinkMeetingsDialog';
import useMeetingSearch, { isSearching } from './MeetingSearch';
import { rangeFor, shiftAnchor, byDate, daysFrom, startOfWeek } from './calendarDates';
import { involvesPartner } from './meetingKind';
import { todayStr } from '../../utils/dates';

export default function Calendar() {
  const { partner: me } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(todayStr());
  const [mode, setMode] = useState('grid');
  const [filter, setFilter] = useState('all');

  // Which modal is open. Exactly one at a time, held as one piece of state so two can
  // never be open at once after a fast double click.
  const [editing, setEditing] = useState(null);   // a meeting, a prefill, or null
  const [opened, setOpened] = useState(null);     // the meeting whose detail is open
  const [cancelling, setCancelling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [task, setTask] = useState(null);         // a TaskModal prefill
  const [linking, setLinking] = useState(null);   // { meeting, mode: 'lead' | 'client' }

  const [query, setQuery] = useState('');
  const [searchVersion, setSearchVersion] = useState(0);
  const searching = isSearching(query);
  const search = useMeetingSearch(query, searchVersion);

  const { from, to } = rangeFor(view, anchor);
  const { data, loading, refreshing, error, reload } = useAsync(
    () => Promise.all([meetingsApi.window(from, to), authApi.partners()]),
    [from, to],
  );
  const [meetings, partners] = data || [[], []];

  // The partner filter, in the browser. It asks about BOTH sides of a meeting: an internal
  // meeting is owned by one partner and held with the other, and it belongs on both their
  // calendars — filtering on the owner alone would hide it from the person it is with.
  const visible = filter === 'all'
    ? meetings
    : meetings.filter((m) => involvesPartner(m, filter));
  const byDay = byDate(visible);

  // Opening the detail keeps the row from the freshly-loaded list, so a summary saved in
  // the dialog is reflected the moment the window reloads behind it.
  const openMeeting = (m) => setOpened(m);
  const current = opened
    ? ([...meetings, ...search.found].find((m) => m.id === opened.id) || opened)
    : null;

  // Booking from empty space in the grid: the day and time clicked become the prefill.
  const bookAt = (date, time) => setEditing({ meeting_date: date, start_time: time });

  const afterChange = async () => { setSearchVersion((v) => v + 1); await reload(); };

  const closeAll = () => {
    setEditing(null); setOpened(null); setCancelling(null); setDeleting(null); setTask(null);
    setLinking(null);
  };

  return (
    <>
      {/* THE HEADER IS TWO TIERS, and the split is by KIND OF ACT rather than by how much
          fits on a line. Navigation and the one action belong with the title — where am I,
          and the verb. The three state groups are all "how am I looking at it" and get
          their own bar, where each can be captioned without crowding the title. */}
      <PageHeader title={he.calendar.title} spread>
        <CalendarNav
          view={view}
          anchor={anchor}
          onAnchor={(delta) => setAnchor((a) => shiftAnchor(view, a, delta))}
          onToday={() => setAnchor(todayStr())}
        />
        <SearchBox value={query} onChange={setQuery} placeholder={he.calendar.search} />
        <Button small onClick={() => setEditing({})}>{he.calendar.newMeeting}</Button>
      </PageHeader>

      <CalendarViewBar
        view={view}
        onView={setView}
        mode={mode}
        onMode={setMode}
        filter={filter}
        onFilter={setFilter}
        partners={partners}
        me={me}
      />

      {searching ? (
        <AsyncBoundary loading={search.loading && !search.found.length} error={search.error}>
          <MeetingList
            meetings={search.found}
            partners={partners}
            onOpen={openMeeting}
            withSummary
            emptyText={he.calendar.searchEmpty}
          />
        </AsyncBoundary>
      ) : (
        <AsyncBoundary loading={loading} error={error}>
          <div className={refreshing ? 'refreshing' : undefined}>
            {mode === 'list' ? (
              <MeetingList meetings={visible} partners={partners} onOpen={openMeeting} />
            ) : (
              <>
                {view === 'month' ? (
                  <MonthGrid
                    anchor={anchor}
                    byDay={byDay}
                    partners={partners}
                    onOpen={openMeeting}
                    onOpenDay={(day) => { setAnchor(day); setView('day'); }}
                  />
                ) : (
                  <TimeGrid
                    days={view === 'day' ? [anchor] : daysFrom(startOfWeek(anchor), 7)}
                    byDay={byDay}
                    partners={partners}
                    onOpen={openMeeting}
                    onPickEmpty={bookAt}
                  />
                )}
                {/* The grid always draws its days, so an empty window says so beneath it
                    rather than replacing the picture — the picture is how you find the day
                    you wanted to book. */}
                {visible.length === 0 && (
                  <EmptyState text={filter === 'all' ? he.calendar.empty : he.calendar.emptyFiltered} />
                )}
              </>
            )}
          </div>
        </AsyncBoundary>
      )}

      {editing && (
        <MeetingModal
          meeting={editing}
          partners={partners}
          me={me}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await afterChange(); }}
        />
      )}

      {current && !editing && !cancelling && !deleting && !task && !linking && (
        <MeetingDetail
          meeting={current}
          partners={partners}
          onClose={closeAll}
          onChanged={afterChange}
          onEdit={(m) => setEditing(m)}
          onCancelRequest={(m) => setCancelling(m)}
          onDeleteRequest={(m) => setDeleting(m)}
          // A follow-up carries the same client, job and owner, and remembers where it came
          // from. It names NO date, so the form's own default applies and it opens on today
          // — "when" is the open question here, and today is a day you can see the free
          // hours for and overwrite, which an empty date is not.
          onFollowUpMeeting={(m) => {
            setOpened(null);
            setEditing({
              kind: m.kind,
              client_id: m.client_id,
              related_job_id: m.related_job_id,
              owner_partner_id: m.owner_partner_id,
              with_partner_id: m.with_partner_id,
              external_name: m.external_name,
              external_details: m.external_details,
              follow_up_of_meeting_id: m.id,
            });
          }}
          // The SHARED task form, opened in exactly the shape the board opens it in. The
          // tasks table is untouched by the calendar.
          onAddTask={(m) => {
            setOpened(null);
            // No client (an internal or external meeting) → an internal task, which links
            // to nothing, so the meeting's job is not carried over.
            setTask(m.client_id
              ? { related_client_id: m.client_id, related_job_id: m.related_job_id }
              : { related_client_id: '' });
          }}
          // The lead flow belongs to the leads screen, so this goes there rather than
          // owning a second way to create one.
          onAddLead={() => { closeAll(); navigate('/leads'); }}
          // Someone outside the system becomes a lead here, or is linked to one that exists,
          // and their meetings move onto that card (LinkMeetingsDialog).
          onMakeLead={(m) => setLinking({ meeting: m, mode: 'lead' })}
          onLinkClient={(m) => setLinking({ meeting: m, mode: 'client' })}
        />
      )}

      {linking && (
        <LinkMeetingsDialog
          meeting={linking.meeting}
          mode={linking.mode}
          onClose={() => setLinking(null)}
          onLinked={async () => { setLinking(null); await afterChange(); }}
        />
      )}

      {cancelling && (
        <CancelMeetingModal
          meeting={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={async (reason) => {
            await meetingsApi.setStatus(cancelling.id, 'cancelled', reason);
            setCancelling(null);
            setOpened(null);
            await afterChange();
          }}
        />
      )}

      {deleting && (
        <MeetingDeleteModal
          meeting={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            await meetingsApi.remove(deleting.id);
            setDeleting(null);
            setOpened(null);
            await afterChange();
          }}
        />
      )}

      {task && (
        <TaskModal
          task={task}
          onClose={() => setTask(null)}
          onSaved={() => setTask(null)}
        />
      )}
    </>
  );
}
